import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';

import { Text, Tooltip } from '@shared/components';
import { QuoteAutoReplyScope, QuoteEventType } from '@shared/generated/graphql';
import { QuoteAutoReplySentSource } from 'clerk_common/types';
import { uniq } from 'lodash';
import { PiSparkle } from 'react-icons/pi';
import { FeedQuoteEvent, QuoteEventsFeedItem } from '../../types';
import { FeedItemTimestamp } from '../feed-item/FeedItem';

const ROOT = makeRootClassName('QuoteEvents');
const el = makeElementClassNameFactory(ROOT);

const getActivityLabel = (item: QuoteEventsFeedItem) => {
  const event = item.data.quoteEvents[0];
  const type = event.type;
  const metadata = event.metadata;
  const createdByDisplay = item.createdByDisplay;
  const count = uniq(
    item.data.quoteEvents.map((event) => event.quoteId)
  ).length;

  if (count === 0) return;

  const markAsString = `marked ${count == 1 ? 'a' : count} quote${
    count <= 1 ? '' : 's'
  }`;

  switch (type) {
    case QuoteEventType.MARKED_AS_LOST:
      return `${createdByDisplay} ${markAsString} lost`;
    case QuoteEventType.MARKED_AS_WON:
      return `${createdByDisplay} ${markAsString} won`;
    case QuoteEventType.MARKED_AS_QUOTED:
      return `${createdByDisplay} ${markAsString} quoted`;
    case QuoteEventType.AUTO_REPLY_EVALUATOR_RESPONSE: {
      const scope = event.metadata?.scope;
      const verb = scope === QuoteAutoReplyScope.DRAFT ? 'draft' : 'send';
      return `Vooma decided to ${
        event.metadata?.decision ? `${verb} reply.` : `not ${verb} reply.`
      }`;
    }
    case QuoteEventType.AUTO_REPLY_DRAFTED:
      return 'Vooma drafted an auto reply';
    case QuoteEventType.AUTO_REPLY_SENT:
      return getAutoReplySentTrailMessage(event.metadata);
    case QuoteEventType.AUTO_REPLY_DRAFT_FAILURE:
      return 'Vooma failed to draft an auto reply';
    case QuoteEventType.AUTO_REPLY_SEND_FAILURE:
      return 'Vooma failed to send an auto reply';
    case QuoteEventType.USER_CONFIRMED:
      return getUserConfirmedDetail(metadata);
    default:
      return undefined;
  }
};

enum JobConfirmationReason {
  AUTO_CONFIRM_THRESHOLD_SURPASSED = 'AUTO_CONFIRM_THRESHOLD_SURPASSED',
  AGENT_REPLY_ALLOWED = 'AGENT_REPLY_ALLOWED',
  AUTO_REPLY_ALLOWED = 'AUTO_REPLY_ALLOWED',
  AUTO_DRAFT_ALLOWED = 'AUTO_DRAFT_ALLOWED',
  USER_REQUESTED = 'USER_REQUESTED',
  UNKNOWN = 'UNKNOWN',
}

type QuoteEventUserConfirmedMetadata = {
  type: QuoteEventType.USER_CONFIRMED;
  confirmationReason: JobConfirmationReason;
};

export const getAutoReplySentTrailMessage = (
  metadata?: { source?: string } | null
) =>
  metadata?.source === QuoteAutoReplySentSource.USER_SENT_DRAFT
    ? 'Auto draft sent by user'
    : 'Vooma sent an auto reply';

export const getUserConfirmedDetail = (
  metadata?: QuoteEventUserConfirmedMetadata | null
) => {
  if (!metadata) return 'Vooma confirmed the opportunity';

  const confirmationReason = metadata?.confirmationReason;

  switch (confirmationReason) {
    case JobConfirmationReason.AUTO_CONFIRM_THRESHOLD_SURPASSED:
      return 'Vooma confirmed the opportunity due to auto confirm settings';
    case JobConfirmationReason.AGENT_REPLY_ALLOWED:
      return 'Vooma confirmed the opportunity to attempt agentic quoting';
    case JobConfirmationReason.AUTO_REPLY_ALLOWED:
      return 'Vooma confirmed the opportunity to attempt to auto reply';
    case JobConfirmationReason.AUTO_DRAFT_ALLOWED:
      return 'Vooma confirmed the opportunity to attempt to auto draft';
    case JobConfirmationReason.USER_REQUESTED:
      return 'User confirmed the opportunity';
    case JobConfirmationReason.UNKNOWN:
      return 'Vooma confirmed the opportunity for an unknown reason';
  }
};

type QuoteEventsFeedItemProps = {
  item: QuoteEventsFeedItem;
};
export const QuoteEventItem = ({ item }: QuoteEventsFeedItemProps) => {
  const renderQuoteEventItem = (events: FeedQuoteEvent[]) => {
    const activityLabel = getActivityLabel(item);

    if (!activityLabel) {
      return null;
    }

    return (
      <div>
        <div className={el`quote-event-details`}>
          {activityLabel ? (
            <Tooltip content={activityLabel}>
              <div className={el`quote-event`}>
                <PiSparkle size={14} className={el`quote-event-icon`} />
                <Text type="body-xs" className="truncate">
                  {activityLabel}
                </Text>
              </div>
            </Tooltip>
          ) : null}
          <FeedItemTimestamp timestamp={events[0].createdAt} />
        </div>
      </div>
    );
  };

  return (
    <div className={el`quote-event-root`}>
      {renderQuoteEventItem(item.data.quoteEvents)}
    </div>
  );
};
